import { useEffect } from 'react';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import type { SelfReportedRegistration } from '@models/api';
import { registrationDomain, type RegistrationCelebration } from '@utils/registrationCelebration';

interface UseTicketQuestionArgs {
  /** The server asked "have a ticket?" (TECH-575). */
  registration: RegistrationCelebration;
  /**
   * Arrival attribution of a post-auth Going (the stored intent's `?source=`), added to the view
   * event when present so per-source counts can combine it with event_going_welcome_viewed.
   */
  source?: string | null;
  /**
   * The member answered. The host saves it and closes, and returns true; it returns false for a tap
   * it refuses (its entrance veto), and nothing is opened or reported then. Called first,
   * synchronously in the tap.
   */
  onAnswer: (answer: SelfReportedRegistration) => boolean;
}

export interface TicketQuestion {
  registration: RegistrationCelebration;
  /** Derived from the link it opens, never read from the payload (see RegistrationCelebration). */
  domain: string;
  live: boolean;
  /** True when the host took the answer. */
  answer: (value: SelfReportedRegistration) => boolean;
  trackLinkClicked: () => void;
}

/**
 * The ticket question's state and analytics (TECH-575), shared by the ticketed Going drawer and the
 * Going PIN sheet's last step. It renders nothing: `TicketQuestionText` and `TicketQuestionAnswers`
 * (`./TicketQuestionContent`) take the handle it returns.
 */
export function useTicketQuestion({
  registration,
  source,
  onAnswer,
}: UseTicketQuestionArgs): TicketQuestion {
  const { trackEvent } = useAmplitudeTracking();
  const { event_id, phase, surface } = registration;
  const domain = registrationDomain(registration);

  useEffect(() => {
    trackEvent(ANALYTICS_EVENTS.event_registration_sheet_shown, {
      event_id,
      phase,
      surface,
      // Spread only when present, as EventGoingCelebrationDrawer does: the base payload already
      // carries the session's `source`, and a null here would overwrite it.
      ...(source ? { source } : {}),
    });
  }, [trackEvent, event_id, phase, surface, source]);

  const answer = (value: SelfReportedRegistration): boolean => {
    if (!onAnswer(value)) return false;
    trackEvent(ANALYTICS_EVENTS.event_registration_self_reported, {
      event_id,
      answer: value,
      phase,
      surface,
    });
    return true;
  };

  const trackLinkClicked = () => {
    trackEvent(ANALYTICS_EVENTS.event_registration_link_clicked, {
      event_id,
      surface: 'going_sheet',
    });
  };

  return { registration, domain, live: phase === 'live', answer, trackLinkClicked };
}
